<template>
  <div class="form-container v-theme--light">
    <v-row class="">
      <v-col md="4" sm="12">
        <div class="form-width">
          <FirmwareVersionAutoComplete
            v-model="selectedFirmware"
            :errorMessages="errors?.firmware"
          />
          <validation-errors class="m-2" :messages="errors.activate_date">
            <VueDatePicker
              class="mb-0"
              :formats="{ input: 'yyyy/MM/dd' }"
              :locale="ja"
              :placeholder="i18n.global.t('pages.deviceFirmware.applyDate')"
              v-model="form.activate_date"
              :errorMessage="errors?.activate_date?.length ?? 0 > 0"
            />
          </validation-errors>
        </div>
      </v-col>
      <v-col md="4" sm="12" class="v-theme--light">
        <v-textarea
          label="メモ"
          readonly
          :value="selectedFirmware?.memo"
          persistent-placeholder="true"
          rows="3"
        ></v-textarea>
      </v-col>
      <v-col md="4" cols="12">
        <div class="float-right">
          <v-btn class="ma-2 reset-btn" @click="clearForm">
            {{ i18n.global.t('common.cancelBtn') }}
          </v-btn>
          <v-btn
            v-if="updateAuthority"
            class="ma-2 exec-btn"
            @click="validateAndSendForm"
            :disabled="isAttachButtonDisabled"
            :style="{
              backgroundColor: isFirmwareDeviceAttaching ? '#B3B8BB' : '',
            }"
          >
            {{ attachBtnText }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import getFirmwareVersionsResponse from '@/types/responses/getFirmwareVersionsResponse';
import FirmwareVersionsType from '@/types/FirmwareVersionsType';
import FirmwareVersionAutoComplete from '@/components/AutoCompletes/FirmwareVersionAutoComplete.vue';
import ValidationErrors from '@/components/Common/ValidationErrors.vue';
import '@vuepic/vue-datepicker/dist/main.css'
import { ja } from 'date-fns/locale'
import i18n from '@/lang/i18n';

export default defineComponent({
  setup() {
    return {
      i18n,
      ja
    };
  },
  data() {
    return {
      selectedFirmware: undefined as FirmwareVersionsType | undefined,
      form: {
        firmware_id: undefined as number | undefined,
        activate_date: new Date() as Date | undefined,
      },
      errors: {} as {
        firmware?: Array<string>;
        activate_date?: Array<string>;
      },
    };
  },
  emits: ['close-modal', 'send-form', 'select-firmware'],
  props: {
    isFirmwareDeviceAttaching: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    isAttachButtonDisabled: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    availableToSelectFirmwares: {
      type: Array as PropType<getFirmwareVersionsResponse>,
      required: true,
    },
    updateAuthority: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
  },
  name: 'AttachFirmwareForm',
  mounted() {
    //
  },
  computed: {
    isValide(): boolean {
      return Object.keys(this.errors).length == 0;
    },
    attachBtnText(): string {
      return this.isFirmwareDeviceAttaching ?
        i18n.global.t('pages.deviceFirmware.form.button.applying') :
        i18n.global.t('pages.deviceFirmware.form.button.apply');
    },
    isFirmwareIDValide(): string | boolean {
      if (this.selectedFirmware === undefined || this.selectedFirmware === null) {
        return i18n.global.t('pages.deviceFirmware.form.validate.selectFirmware');
      }
      return true;
    },
    isActivateDateValide(): string | boolean {
      if (
        this.form.activate_date == undefined ||
        this.form.activate_date === null
      ) {
        return i18n.global.t('pages.deviceFirmware.form.validate.selectApplyDate');
      }
      return true;
    },
    isNeedToResetSelectedFirmwareID: function (): boolean {
      if (!this.selectedFirmware) {
        return false;
      }

      const selectedFirmwareAvailable = this.availableToSelectFirmwares.find(
        (firmware) => {
          return firmware.id == this.form.firmware_id;
        }
      );

      return !selectedFirmwareAvailable;
    },
  },
  watch: {
    selectedFirmware: {
      handler() {
        this.form.firmware_id = this.selectedFirmware?.id;
      },
      deep: true,
    },
    form: {
      handler() {
        this.validateForm();
      },
      deep: true,
    },
    availableToSelectFirmwares: {
      handler() {
        if (this.isNeedToResetSelectedFirmwareID) {
          this.selectedFirmware = undefined;
        }
      },
      deep: true,
    },
  },
  methods: {
    validateAndSendForm(): void {
      if (this.isFirmwareDeviceAttaching) {
        return;
      }
      this.validateForm();
      if (this.isValide) {
        this.sendForm();
      }
    },
    validateForm(): void {
      this.errors = {};

      if (this.isActivateDateValide !== true) {
        if (!this.errors.activate_date) this.errors.activate_date = [];
        this.errors.activate_date.push(this.isActivateDateValide as string);
      }

      if (this.isFirmwareIDValide !== true) {
        if (!this.errors.firmware) this.errors.firmware = [];
        this.errors.firmware.push(this.isFirmwareIDValide as string);
      }
    },
    clearForm(): void {
      this.selectedFirmware = undefined;
      this.form.activate_date = undefined;
    },
    sendForm(): void {
      this.$emit('send-form', this.form);
    },
  },
  components: {
    VueDatePicker,
    FirmwareVersionAutoComplete,
    ValidationErrors,
  },
});
</script>

<style scoped>
.v-btn--disabled.v-btn--variant-elevated,
.v-btn--disabled.v-btn--variant-flat {
  background-color: rgb(71, 170, 196) !important;
}
</style>
